import React, { useState, useMemo } from 'react';
import { getProductFirstImageUrl } from '../../utils/imageHelper';

const CartItemImage = ({ item }) => {
  const [hasError, setHasError] = useState(false);
  const imageUrl = useMemo(() => {
    return getProductFirstImageUrl(item) || item.imagen || item.foto || null;
  }, [item]);

  if (!imageUrl || hasError) {
    return (
      <div className="ecom-cart-item-img d-flex align-items-center justify-content-center text-muted">
        <i className="bi bi-box-seam fs-4"></i>
      </div>
    );
  }

  return (
    <img
      src={imageUrl}
      alt={item.d_artic || 'Producto'}
      className="ecom-cart-item-img"
      onError={() => setHasError(true)}
    />
  );
};

const EcommerceCartDrawer = ({
  isOpen,
  onClose,
  cartItems,
  onUpdateQuantity,
  onRemoveItem,
  onClearCart,
  onCheckout,
  canSeePrices,
  onLoginClick
}) => {
  // Calcular subtotal de ítems con precio
  const subtotal = cartItems.reduce((acc, item) => {
    const price = Number(item.unitPrice || 0);
    return acc + price * item.quantity;
  }, 0);

  const totalItemsCount = cartItems.reduce((acc, item) => acc + item.quantity, 0);

  return (
    <>
      {/* Backdrop oscuro con blur */}
      <div
        className={`ecom-cart-backdrop ${isOpen ? 'open' : ''}`}
        onClick={onClose}
      />

      {/* Panel lateral deslizable */}
      <div className={`ecom-cart-drawer ${isOpen ? 'open' : ''}`}>
        
        {/* Encabezado */}
        <div className="ecom-cart-header">
          <div className="d-flex align-items-center gap-2">
            <i className="bi bi-cart3 fs-4 text-primary"></i>
            <h5 className="mb-0 fw-bold text-dark">Mi Carrito</h5>
            <span className="badge bg-primary-subtle text-primary rounded-pill ms-1">
              {totalItemsCount} {totalItemsCount === 1 ? 'ítem' : 'ítems'}
            </span>
          </div>
          <button
            type="button"
            className="ecom-drawer-close-btn"
            onClick={onClose}
            title="Cerrar carrito"
            aria-label="Cerrar carrito"
          >
            <i className="bi bi-x-lg"></i>
          </button>
        </div>

        {/* Cuerpo / Listado de Ítems */}
        <div className="ecom-cart-body">
          {cartItems.length === 0 ? (
            <div className="d-flex flex-column align-items-center justify-content-center text-center h-100 py-5">
              <div className="bg-light p-4 rounded-circle mb-3">
                <i className="bi bi-cart-x text-muted display-4"></i>
              </div>
              <h6 className="fw-bold text-dark mb-1">Tu carrito está vacío</h6>
              <p className="text-muted small mb-4" style={{ maxWidth: '240px' }}>
                Explorá los productos de nuestro catálogo y agregalos aquí.
              </p>
              <button className="btn btn-primary btn-sm rounded-pill px-4" onClick={onClose}>
                <i className="bi bi-shop me-1"></i> Explorar Catálogo
              </button>
            </div>
          ) : (
            <div className="d-flex flex-column">
              {cartItems.map((item, index) => {
                const itemKey = `${item.c_artic}_${item.selectedVariant?.id || 'std'}_${item.selectedSize?.id || item.selectedSize?.nombre || 'nosize'}`;
                const itemSubtotal = (item.unitPrice || 0) * item.quantity;

                return (
                  <div key={itemKey || index} className="ecom-cart-item">
                    {/* Imagen o Ícono Resuelto */}
                    <CartItemImage item={item} />

                    {/* Detalles */}
                    <div className="ecom-cart-item-details">
                      <div className="d-flex align-items-start justify-content-between gap-2">
                        <span className="ecom-cart-item-title" title={item.d_artic}>
                          {item.d_artic}
                        </span>
                        <button
                          type="button"
                          className="btn btn-sm btn-link text-muted hover-danger p-0 text-decoration-none"
                          onClick={() => onRemoveItem(itemKey)}
                          title="Eliminar del carrito"
                        >
                          <i className="bi bi-trash3"></i>
                        </button>
                      </div>

                      {/* Variante y Talle */}
                      <div className="ecom-cart-item-variant d-flex flex-wrap align-items-center gap-1">
                        {item.selectedVariant?.name && item.selectedVariant?.name !== 'Estándar' && (
                          <span className="badge bg-light text-secondary border">
                            {item.selectedVariant.name}
                          </span>
                        )}
                        {item.selectedSize?.nombre && (
                          <span className="badge bg-primary-subtle text-primary border border-primary-subtle">
                            Talle: {item.selectedSize.nombre}
                          </span>
                        )}
                        {(!item.selectedVariant?.name || item.selectedVariant?.name === 'Estándar') && !item.selectedSize?.nombre && (
                          <span className="badge bg-light text-secondary border">
                            Estándar
                          </span>
                        )}
                        <span className="font-monospace text-muted small">{item.c_artic}</span>
                      </div>

                      {/* Controles de Cantidad y Precio */}
                      <div className="d-flex align-items-center justify-content-between mt-2">
                        <div className="ecom-qty-picker" style={{ height: '30px' }}>
                          <button
                            type="button"
                            className="ecom-qty-btn"
                            style={{ width: '28px', height: '28px' }}
                            onClick={() => onUpdateQuantity(itemKey, item.quantity - 1)}
                            disabled={item.quantity <= 1}
                          >
                            <i className="bi bi-dash"></i>
                          </button>
                          <input
                            type="text"
                            className="ecom-qty-input"
                            style={{ width: '36px', height: '28px', fontSize: '0.82rem' }}
                            value={item.quantity}
                            onChange={(e) => {
                              const val = parseInt(e.target.value, 10);
                              const max = (item.maxStock !== undefined && item.maxStock !== null && item.maxStock > 0) ? item.maxStock : Infinity;
                              const safeVal = isNaN(val) || val < 1 ? 1 : Math.min(val, max);
                              onUpdateQuantity(itemKey, safeVal);
                            }}
                          />
                          <button
                            type="button"
                            className="ecom-qty-btn"
                            style={{ width: '28px', height: '28px' }}
                            onClick={() => onUpdateQuantity(itemKey, item.quantity + 1)}
                            disabled={item.maxStock > 0 && item.quantity >= item.maxStock}
                          >
                            <i className="bi bi-plus"></i>
                          </button>
                        </div>

                        {/* Precio o Indicador */}
                        <div>
                          {canSeePrices ? (
                            Number(item.unitPrice || 0) > 0 ? (
                              <div className="text-end">
                                <span className="ecom-cart-item-price">
                                  ${Number(itemSubtotal).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </span>
                                <span className="text-muted small d-block" style={{ fontSize: '0.68rem' }}>
                                  (${Number(item.unitPrice).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} c/u)
                                </span>
                              </div>
                            ) : (
                              <span className="badge bg-light text-muted border small" title="Artículo sin precio fijado en la lista activa">
                                A cotizar ($0,00)
                              </span>
                            )
                          ) : (
                            <span className="badge bg-secondary-subtle text-secondary small">
                              Precio tras login
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Pie del Carrito */}
        {cartItems.length > 0 && (
          <div className="ecom-cart-footer">
            {canSeePrices ? (
              <div className="mb-3">
                <div className="d-flex justify-content-between text-muted small mb-1">
                  <span>Subtotal neto:</span>
                  <span>${Number(subtotal).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </div>
                <div className="d-flex justify-content-between text-dark fw-bold fs-5 pt-2 border-top">
                  <span>Total estimado:</span>
                  <span className="text-primary">${Number(subtotal).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </div>
              </div>
            ) : (
              <div className="alert alert-light border small text-muted p-2 mb-3 d-flex align-items-center justify-content-between">
                <div>
                  <i className="bi bi-lock-fill text-warning me-1"></i>
                  <span>Precios bloqueados</span>
                </div>
                <button
                  type="button"
                  className="btn btn-link text-primary btn-sm p-0 fw-semibold text-decoration-none"
                  onClick={onLoginClick}
                >
                  Registrate o Ingresá
                </button>
              </div>
            )}

            {/* Botones de Acción */}
            <div className="d-grid gap-2">
              <button
                type="button"
                className="btn btn-primary py-2 fw-bold rounded-3 shadow-sm d-flex align-items-center justify-content-center gap-2"
                onClick={onCheckout}
              >
                {canSeePrices ? (
                  <>
                    <i className="bi bi-bag-check-fill"></i>
                    <span>Finalizar Pedido</span>
                  </>
                ) : (
                  <>
                    <i className="bi bi-person-check-fill"></i>
                    <span>Identificate para Finalizar Pedido</span>
                  </>
                )}
              </button>

              <button
                type="button"
                className="btn btn-link text-danger btn-sm text-decoration-none py-1"
                onClick={onClearCart}
              >
                <i className="bi bi-trash me-1"></i> Vaciar Carrito
              </button>
            </div>
          </div>
        )}

      </div>
    </>
  );
};

export default EcommerceCartDrawer;
